import { useEffect, useMemo, useState } from 'react';

import { TailwindWrapper } from '@utils/TailwindWrapper';
import { Text, Button } from '@ui-component-library';

import SellTimberMap from './components/SellTimberMap';
import { newSellTimberMarkerId } from './helpers/api';
import { loadSellTimberMapPropertyBundle } from './helpers/sellTimberMapPropertyLoad';
import {
  buildGrotStep2InitialState,
  MAP_MODES
} from './helpers/sellTimberMapStep2State';
import { SELL_TIMBER_MARKER_ICON_OPTIONS } from './helpers/sellTimberMarkerIcons';
import {
  SELL_TIMBER_GROT_MARKER_ICON,
  SELL_TIMBER_GROT_MARKER_NAME,
  filterGrotBiobransleMarkers,
  hasGrotBiobransleMarkerPlacement
} from './helpers/sellTimberShared';

const GROT_MARKER_OPT = SELL_TIMBER_MARKER_ICON_OPTIONS[SELL_TIMBER_GROT_MARKER_ICON];

export default function SellTimberGrotStep2({ wizard, dispatch }) {
  const selectedProperty = wizard.adCreateData?.selectedProperty ?? null;
  const initialMapState = wizard.geometry;

  const [mapState, setMapState] = useState(() =>
    buildGrotStep2InitialState(initialMapState, wizard.mapPropertyCache, selectedProperty?.ref)
  );

  const grotMarkers = filterGrotBiobransleMarkers(mapState.placedMarkers);
  const hasMarker = grotMarkers.length > 0;

  const loading = mapState.propertyFetch.status === 'loading';
  const loadError = mapState.propertyFetch.error;

  /** Apply wizard map cache when preload completes or when returning to this step. */
  useEffect(() => {
    const c = wizard.mapPropertyCache;
    const ref = selectedProperty?.ref;
    if (!ref || !c || String(c.ref) !== String(ref)) return;
    if (c.propertyFetch?.status !== 'success') return;

    setMapState((s) => ({
      ...s,
      sections: Array.isArray(c.sections) ? c.sections : [],
      borderCoords: c.borderCoords ?? null,
      propertyGeojson: c.propertyGeojson ?? null,
      fellingApplicationsGeojson: c.fellingApplicationsGeojson ?? null,
      propertyFetch: c.propertyFetch,
      fellingFetch: c.fellingFetch
    }));
  }, [wizard.mapPropertyCache, selectedProperty?.ref]);

  useEffect(() => {
    const ref = selectedProperty?.ref;
    if (!ref) {
      setMapState((s) => ({
        ...s,
        propertyFetch: { status: 'error', error: 'Ingen fastighet vald. Gå tillbaka och välj en fastighet.' }
      }));
      return undefined;
    }

    const c = wizard.mapPropertyCache;
    if (c && String(c.ref) === String(ref) && c.propertyFetch?.status === 'success') {
      return undefined;
    }
    if (c && String(c.ref) === String(ref) && c.propertyFetch?.status === 'loading') {
      void loadSellTimberMapPropertyBundle(ref).then((bundle) => {
        dispatch({ type: 'MAP_PROPERTY_CACHE_SET', payload: bundle });
      });
      return undefined;
    }

    dispatch({ type: 'MAP_PROPERTY_CACHE_LOADING', ref });
    void loadSellTimberMapPropertyBundle(ref).then((bundle) => {
      dispatch({ type: 'MAP_PROPERTY_CACHE_SET', payload: bundle });
    });
    return undefined;
    // Only re-run when property ref changes; cache is read to avoid duplicate fetches.
  }, [selectedProperty?.ref, dispatch]);

  function onMarkerAddAt(latlng) {
    const feature = {
      type: 'Feature',
      properties: {
        sellTimberMarkerId: newSellTimberMarkerId(),
        name: SELL_TIMBER_GROT_MARKER_NAME,
        icon: SELL_TIMBER_GROT_MARKER_ICON
      },
      geometry: { type: 'Point', coordinates: [latlng.lng, latlng.lat] }
    };
    setMapState((s) => ({
      ...s,
      continueError: null,
      removeMode: false,
      placedMarkers: [...s.placedMarkers, feature]
    }));
  }

  function onMarkerSelectForMove(markerId) {
    setMapState((s) => ({ ...s, continueError: null, markerMoveTargetId: String(markerId) }));
  }

  function onMarkerMoveTo(markerId, latlng) {
    const id = String(markerId);
    setMapState((s) => ({
      ...s,
      continueError: null,
      markerMoveTargetId: null,
      placedMarkers: s.placedMarkers.map((f) => {
        if (String(f?.properties?.sellTimberMarkerId) !== id) return f;
        return {
          ...f,
          geometry: { type: 'Point', coordinates: [latlng.lng, latlng.lat] }
        };
      })
    }));
  }

  function onRemoveMarker(markerId) {
    const id = String(markerId);
    setMapState((s) => {
      const placedMarkers = s.placedMarkers.filter(
        (f) => String(f?.properties?.sellTimberMarkerId) !== id
      );
      return {
        ...s,
        continueError: null,
        markerMoveTargetId: s.markerMoveTargetId === id ? null : s.markerMoveTargetId,
        removeMode: placedMarkers.length > 0 ? s.removeMode : false,
        placedMarkers
      };
    });
  }

  const mapStatusText = useMemo(() => {
    const count = grotMarkers.length;
    if (mapState.removeMode) {
      return 'Borttagningsläge: klicka på en biobränsle-markör på kartan för att ta bort den. Klicka på Klar när du är färdig.';
    }
    if (mapState.markerMoveTargetId) {
      return 'Klicka på kartan där du vill flytta den valda biobränsle-markören.';
    }
    if (count > 0) {
      return count === 1
        ? 'En biobränsle-markör är placerad. Klicka på kartan för att lägga till fler, eller klicka på markören för att flytta den.'
        : `${count} biobränsle-markörer är placerade. Klicka på kartan för att lägga till fler, eller klicka på en markör för att flytta den.`;
    }
    return 'Klicka på kartan där biobränslet finns. Du kan placera flera markörer — klicka på kartan igen för varje plats.';
  }, [mapState.removeMode, mapState.markerMoveTargetId, grotMarkers.length]);

  const showMap =
    mapState.propertyFetch.status === 'success' && Boolean(selectedProperty?.ref) && !loadError;

  const buildGeometryPayload = () => ({
    mapMode: MAP_MODES.marker,
    sections: mapState.sections,
    borderCoords: mapState.borderCoords,
    selectedSectionLocalIds: [],
    selectedFellingApplicationIds: [],
    fellingApplicationsGeojson: mapState.fellingApplicationsGeojson,
    drawnPolygons: [],
    placedMarkers: mapState.placedMarkers,
    customAreaFromAd: null,
    selectedMarkerIcon: SELL_TIMBER_GROT_MARKER_ICON
  });

  const handleBackToStep1 = () => {
    setMapState((s) => ({ ...s, continueError: null }));
    dispatch({ type: 'SET_GEOMETRY', payload: buildGeometryPayload() });
    dispatch({ type: 'SET_STEP', step: 1 });
  };

  return (
    <>
      <TailwindWrapper>
        <div className="w-160 max-w-full mx-auto">
          <div className="mx-auto flex min-w-full max-w-full flex-col gap-6 p-6">
            <Text variant="text-xl/leading-normal/medium">Var finns biobränslet?</Text>

            {selectedProperty?.designation ? (
              <Text variant="text-base/leading-normal/normal" className="capitalize text-charcoal-700">
                Fastighet: {selectedProperty.designation}
              </Text>
            ) : null}

            {loadError ? (
              <Text variant="text-sm/leading-normal/normal" className="text-red-700">
                {loadError}
              </Text>
            ) : null}

            {loading ? (
              <Text variant="text-sm/leading-normal/normal" className="text-charcoal-600">
                Laddar karta…
              </Text>
            ) : null}

            {!loading && !loadError && selectedProperty?.ref ? (
              <>
                <div className="flex flex-col gap-2 rounded-lg border border-charcoal-200 bg-cloud-200/40 p-4">
                  <div className="flex items-center gap-3">
                    {GROT_MARKER_OPT ? (
                      <img
                        src={`${Window.static_path}${GROT_MARKER_OPT.path}`}
                        alt=""
                        className="h-9 w-9 shrink-0 object-contain"
                      />
                    ) : null}
                    <Text variant="text-sm/leading-normal/medium">{SELL_TIMBER_GROT_MARKER_NAME}</Text>
                  </div>
                  <Text variant="text-sm/leading-normal/normal" className="text-charcoal-600">
                    Placera en eller flera markörer på kartan som visar var biobränslet finns på fastigheten.
                  </Text>
                </div>

                <Text variant="text-sm/leading-normal/normal" className="text-charcoal-600">
                  {mapStatusText}
                </Text>

                <div className="flex flex-wrap items-center gap-3">
                  {mapState.removeMode ? (
                    <Button
                      tw
                      type="button"
                      variant="primary"
                      size="medium"
                      onClick={() => setMapState((s) => ({ ...s, removeMode: false }))}
                    >
                      Klar
                    </Button>
                  ) : (
                    <Button
                      tw
                      type="button"
                      variant="secondary"
                      size="medium"
                      disabled={!hasMarker}
                      onClick={() =>
                        setMapState((s) => ({ ...s, removeMode: true, markerMoveTargetId: null }))
                      }
                    >
                      Ta bort markör
                    </Button>
                  )}
                </div>
              </>
            ) : null}
          </div>
        </div>
      </TailwindWrapper>

      {showMap ? (
        <div
          style={{
            width: '100%',
            maxWidth: '800px',
            margin: '0 auto',
            padding: '0 24px 24px'
          }}
        >
          <SellTimberMap
            selection={{
              mapMode: MAP_MODES.marker,
              sections: mapState.sections,
              borderCoords: mapState.borderCoords,
              fellingApplicationsGeojson: mapState.fellingApplicationsGeojson,
              selectedSectionLocalIds: [],
              selectedFellingApplicationIds: [],
              drawnPolygons: [],
              placedMarkers: mapState.placedMarkers,
              markerMoveTargetId: mapState.markerMoveTargetId
            }}
            handlers={{
              onMarkerAddAt,
              onMarkerSelectForMove,
              onMarkerMoveTo,
              onRemoveMarker
            }}
            options={{
              removeMode: mapState.removeMode,
              mapId: 'sell_timber_grot_vb_leaflet',
              markerEmptyHint: 'Klicka på kartan där biobränslet finns.',
              markerHasMarkersHint: 'Klicka på kartan för fler markörer, eller på en markör för att flytta den.'
            }}
          />
        </div>
      ) : null}

      <TailwindWrapper>
        <div className="w-160 max-w-full mx-auto flex-col gap-3 px-6 pb-6">
          {mapState.continueError ? (
            <Text variant="text-sm/leading-normal/normal" className="text-red-700" role="alert">
              {mapState.continueError}
            </Text>
          ) : null}
          <div className="flex flex-wrap items-center gap-3">
            <Button type="button" variant="tertiary" onClick={handleBackToStep1}>
              Tillbaka
            </Button>
            <Button
              tw
              type="button"
              variant="primary"
              disabled={mapState.removeMode}
              onClick={() => {
                const payload = buildGeometryPayload();
                if (!hasGrotBiobransleMarkerPlacement(payload)) {
                  setMapState((s) => ({
                    ...s,
                    continueError: 'Placera minst en Biobränsle-markör på kartan innan du går vidare.'
                  }));
                  return;
                }
                setMapState((s) => ({ ...s, continueError: null }));
                dispatch({ type: 'SET_GEOMETRY', payload });
                dispatch({ type: 'SET_STEP', step: 3 });
              }}
            >
              Gå vidare
            </Button>
          </div>
        </div>
      </TailwindWrapper>
    </>
  );
}
